import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Flex,
  useToast,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Grid,
  GridItem,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Image,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Progress
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaEye, FaCoins, FaArrowLeft, FaWallet, FaPlus, FaMinus, FaTrash, FaCheck, FaExchangeAlt, FaImage } from 'react-icons/fa';
import { CARD_TEMPLATE, validateCardConfig } from './config/ScratchCardConfig';
import ScratchCardTemplate from './ScratchCardTemplate';

/**
 * ScratchCardCreator
 * 
 * Allows users to create their own scratch card games.
 * They can customize:
 * - Card name and description
 * - Cost and maximum win amount
 * - Background color
 * - Grid size
 * - Number of winning symbols required
 * - Prize structure with their own tokens and NFTs
 * - Set distribution and odds
 * - Mint up to 1000 cards with minimum 50% RTP
 */
const ScratchCardCreator = () => {
  const [cardConfig, setCardConfig] = useState({ ...CARD_TEMPLATE });
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [walletAssets, setWalletAssets] = useState({
    tokens: [],
    nfts: [],
    isLoading: false
  });
  const [prizePool, setPrizePool] = useState([]);
  const [totalMints, setTotalMints] = useState(1000); // Default max mints
  const [rtp, setRtp] = useState(50); // Default minimum RTP
  const [calculatedRtp, setCalculatedRtp] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAsset, setSelectedAsset] = useState(null);
  
  const navigate = useNavigate();
  const toast = useToast();
  
  // Predefined color options
  const colorOptions = [
    { name: 'Blue', value: '#1a365d' },
    { name: 'Green', value: '#1a4d1a' },
    { name: 'Purple', value: '#4B0082' },
    { name: 'Red', value: '#5a0000' },
    { name: 'Gold', value: '#8B6914' },
    { name: 'Black', value: '#000000' }
  ];
  
  // Mock tokens and NFTs for demonstration
  const mockTokens = [
    { id: 'jinu', name: 'JINU', symbol: 'JINU', balance: 1000000, icon: '🪙' },
    { id: 'eth', name: 'Ethereum', symbol: 'ETH', balance: 5.5, icon: '⟠' },
    { id: 'usdc', name: 'USD Coin', symbol: 'USDC', balance: 10000, icon: '💲' },
    { id: 'sol', name: 'Solana', symbol: 'SOL', balance: 100, icon: '◎' },
  ];
  
  const mockNfts = [
    { id: 'nft1', name: 'Palm Heist #1234', collection: 'Palm Heist', image: 'https://via.placeholder.com/150?text=NFT1' },
    { id: 'nft2', name: 'Bored Ape #5678', collection: 'BAYC', image: 'https://via.placeholder.com/150?text=NFT2' },
    { id: 'nft3', name: 'Azuki #9101', collection: 'Azuki', image: 'https://via.placeholder.com/150?text=NFT3' },
    { id: 'nft4', name: 'Doodle #1121', collection: 'Doodles', image: 'https://via.placeholder.com/150?text=NFT4' },
  ];
  
  // Fetch wallet assets
  const fetchWalletAssets = () => {
    setWalletAssets(prev => ({ ...prev, isLoading: true }));
    
    // In a real app, this would call the wallet API
    // For now, we'll use mock data
    setTimeout(() => {
      setWalletAssets({
        tokens: mockTokens,
        nfts: mockNfts,
        isLoading: false
      });
      
      // Skip wallet connected toast to prevent duplicate messages
      console.log("Wallet connected in ScratchCardCreator, assets fetched");
    }, 1500);
  };
  
  // Add prize to the pool
  const addPrize = (asset, quantity) => {
    // Close the modal
    onClose();
    
    // Add the prize to the pool
    setPrizePool(prev => [
      ...prev,
      {
        id: `${asset.id}-${Date.now()}`,
        asset: asset,
        quantity: quantity,
        winners: 1 // Default to 1 winner
      }
    ]);
    
    // Clear selected asset
    setSelectedAsset(null);
    
    // Update RTP calculation
    calculateRtp();
  };
  
  // Remove prize from the pool
  const removePrize = (prizeId) => {
    setPrizePool(prev => prev.filter(prize => prize.id !== prizeId));
    calculateRtp();
  };
  
  // Update prize winners count
  const updatePrizeWinners = (prizeId, winners) => {
    setPrizePool(prev => prev.map(prize => 
      prize.id === prizeId ? { ...prize, winners } : prize
    ));
    calculateRtp();
  };
  
  // Calculate RTP (Return to Player)
  const calculateRtp = () => {
    if (prizePool.length === 0 || totalMints === 0) {
      setCalculatedRtp(0);
      return;
    }
    
    // Calculate total prize value
    const totalPrizeValue = prizePool.reduce((total, prize) => {
      let value = 0;
      if (prize.asset.symbol) {
        // It's a token
        value = prize.quantity * (prize.asset.symbol === 'JINU' ? 1 : prize.asset.symbol === 'ETH' ? 3000 : prize.asset.symbol === 'SOL' ? 150 : 1);
      } else {
        // It's an NFT, estimate value at 1000 JINU for demo
        value = 1000;
      }
      return total + (value * prize.winners);
    }, 0);
    
    // Calculate total cost to mint all cards
    const totalCost = cardConfig.cost * totalMints;
    
    // Calculate RTP
    const calculatedRtp = (totalPrizeValue / totalCost) * 100;
    setCalculatedRtp(calculatedRtp);
  };
  
  // Handle form field changes
  const handleChange = (field, value) => {
    setCardConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle grid size change
  const handleGridSizeChange = (rows, columns) => {
    setCardConfig(prev => ({
      ...prev,
      gridSize: { rows, columns }
    }));
  };
  
  // Handle total mints change
  const handleTotalMintsChange = (value) => {
    setTotalMints(value);
    calculateRtp();
  };
  
  // Handle minimum RTP change
  const handleRtpChange = (value) => {
    setRtp(value);
  };
  
  // Open asset selection modal
  const openAssetModal = (asset) => {
    setSelectedAsset(asset);
    onOpen();
  };
  
  // Validate the card configuration
  const validateCard = () => {
    const { isValid, errors } = validateCardConfig(cardConfig);
    
    // Additional validation for prize pool
    if (prizePool.length === 0) {
      errors.push('You must add at least one prize to the pool');
    }
    
    if (calculatedRtp < rtp) {
      errors.push(`RTP must be at least ${rtp}%. Current RTP: ${calculatedRtp.toFixed(2)}%`);
    }
    
    setErrors(errors);
    return isValid && prizePool.length > 0 && calculatedRtp >= rtp;
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!validateCard()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // In a real app, this would make an API call to create the card
    // For now, we'll just simulate it
    setTimeout(() => {
      toast({
        title: "Card Created!",
        description: "Your scratch card game has been created successfully.",
        status: "success",
        duration: 5000,
        isClosable: true
      });
      
      setIsSubmitting(false);
      navigate('/scratch-cards');
    }, 2000);
  };
  
  // Handle back button
  const handleBack = () => {
    navigate('/scratch-cards');
  };
  
  return (
    <Box p={4}>
      <Button leftIcon={<FaArrowLeft />} mb={4} onClick={handleBack}>
        Back to Marketplace
      </Button>
      
      <VStack spacing={6} align="stretch">
        <Heading size="lg" textAlign="center">Create Your Own Scratch Card Game</Heading>
        
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Creator Rewards!</AlertTitle>
            <AlertDescription>
              You'll earn 20% of all volume generated by your scratch card game.
            </AlertDescription>
          </Box>
        </Alert>
        
        <Flex 
          direction={{ base: 'column', lg: 'row' }} 
          gap={8}
        >
          {/* Form Area */}
          <Box flex="1">
            <VStack spacing={6} align="stretch">
              <Heading size="md">Card Details</Heading>
              
              <FormControl isRequired>
                <FormLabel>Card Name</FormLabel>
                <Input 
                  value={cardConfig.name} 
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., MEGA MILLIONS"
                  maxLength={20}
                />
                <FormHelperText>Maximum 20 characters</FormHelperText>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Input 
                  value={cardConfig.description} 
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="e.g., Win up to 100,000 JINU"
                  maxLength={30}
                />
                <FormHelperText>Maximum 30 characters</FormHelperText>
              </FormControl>
              
              <Divider />
              
              <Heading size="md">Game Economics</Heading>
              
              <FormControl isRequired>
                <FormLabel>Card Cost (JINU)</FormLabel>
                <NumberInput 
                  min={10} 
                  max={1000}
                  value={cardConfig.cost}
                  onChange={(value) => handleChange('cost', parseInt(value))}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Between 10 and 1,000 JINU</FormHelperText>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Maximum Win Amount (JINU)</FormLabel>
                <NumberInput 
                  min={cardConfig.cost * 10} 
                  max={1000000}
                  value={cardConfig.maxWin}
                  onChange={(value) => handleChange('maxWin', parseInt(value))}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Must be at least 10x the card cost</FormHelperText>
              </FormControl>
              
              <Divider />
              
              <Heading size="md">Card Appearance</Heading>
              
              <FormControl>
                <FormLabel>Background Color</FormLabel>
                <Select 
                  value={cardConfig.bgColor}
                  onChange={(e) => handleChange('bgColor', e.target.value)}
                >
                  {colorOptions.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Grid Size</FormLabel>
                <Select 
                  value={`${cardConfig.gridSize.rows}x${cardConfig.gridSize.columns}`}
                  onChange={(e) => {
                    const [rows, columns] = e.target.value.split('x').map(Number);
                    handleGridSizeChange(rows, columns);
                  }}
                >
                  <option value="3x3">3x3 (9 cells)</option>
                  <option value="4x3">4x3 (12 cells)</option>
                  <option value="4x4">4x4 (16 cells)</option>
                </Select>
                <FormHelperText>Number of cells in the scratch grid</FormHelperText>
              </FormControl>
              
              <FormControl>
                <FormLabel>Winning Symbols Required: {cardConfig.winningSymbols}</FormLabel>
                <Slider 
                  min={3} 
                  max={5} 
                  step={1}
                  value={cardConfig.winningSymbols}
                  onChange={(value) => handleChange('winningSymbols', value)}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
                <FormHelperText>Number of matching symbols needed to win</FormHelperText>
              </FormControl>
              
              <Button 
                colorScheme="purple" 
                size="lg" 
                leftIcon={<FaSave />}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Creating..."
              >
                Create Card
              </Button>
              
              {errors.length > 0 && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start">
                    {errors.map((error, index) => (
                      <Text key={index}>{error}</Text>
                    ))}
                  </VStack>
                </Alert>
              )}
            </VStack>
          </Box>
          
          {/* Preview Area */}
          <Box flex="1">
            <VStack spacing={4} align="center">
              <Heading size="md">Card Preview</Heading>
              
              <Badge colorScheme="purple" p={2} mb={2}>
                <HStack>
                  <FaCoins />
                  <Text>Creator Fee: 20%</Text>
                </HStack>
              </Badge>
              
              <Box>
                <ScratchCardTemplate 
                  cardConfig={cardConfig}
                  isPreview={false}
                />
              </Box>
              
              <Text fontSize="sm" color="gray.500" textAlign="center">
                This is how your card will appear to players.
              </Text>
            </VStack>
          </Box>
        </Flex>
      </VStack>
    </Box>
  );
};

export default ScratchCardCreator;
